import { IS_LEFT_DRAWER_OPEN, IS_RIGHT_DRAWER_OPEN } from '../../constants'
import { useBrowserStorage } from 'hooks'
import * as React from 'react'

type AppContextType = {
  drawerOpen: boolean
  toggleDrawer: () => void
  isLive: boolean
  setIsLive: React.Dispatch<React.SetStateAction<boolean>>
  connected: boolean
  setConnected: React.Dispatch<React.SetStateAction<boolean>>
  drawerOpen2: boolean
  toggleDrawer2: (value?: boolean) => void
  rightSidebarWidth: number
  setRightSidebarWidth: (width: number) => void
  centerWidth: number
}

export const AppContext = React.createContext<AppContextType>({
  drawerOpen: false,
  toggleDrawer: () => null,
  isLive: false,
  setIsLive: () => null,
  connected: false,
  setConnected: () => null,
  drawerOpen2: false,
  toggleDrawer2: () => null,
  rightSidebarWidth: 350,
  setRightSidebarWidth: () => null,
  centerWidth: 800
})

export const useAppContext = () => React.useContext(AppContext)

const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isRightDrawerOpen, setIsRightDrawerOpen] = useBrowserStorage(
    IS_RIGHT_DRAWER_OPEN,
    'local',
    true
  )
  const [isLeftDrawerOpen, setIsLeftDrawerOpen] = useBrowserStorage(
    IS_LEFT_DRAWER_OPEN,
    'local',
    false
  )

  const [drawerOpen, setDrawerOpen] = React.useState(isLeftDrawerOpen || false)
  // The right panel defaults to open and remembers it — right for a desktop,
  // but on a phone the panel is full width and covered every page, including
  // a first visit. Narrow screens start closed; the saved desktop preference
  // is left untouched.
  const [drawerOpen2, setDrawerOpen2] = React.useState(
    Boolean(isRightDrawerOpen) && !(typeof window !== 'undefined' && window.innerWidth < 768)
  )

  const [savedRightSidebarWidth, setSavedRightSidebarWidth] = useBrowserStorage<number>(
    'RIGHT_SIDEBAR_WIDTH',
    'local',
    350
  )
  const [rightSidebarWidth, setRightSidebarWidthState] = React.useState<number>(
    savedRightSidebarWidth || 350
  )

  const setRightSidebarWidth = React.useCallback(
    (width: number) => {
      setRightSidebarWidthState(width)
      setSavedRightSidebarWidth(width)
    },
    [setSavedRightSidebarWidth]
  )

  const [windowWidth, setWindowWidth] = React.useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  )

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    // `resize` fires continuously while dragging a window edge, and this
    // provider sits above the whole app — writing state on every event
    // re-rendered every screen dozens of times a second. Coalesce to one write
    // per frame, and skip it entirely when the width hasn't actually changed
    // (vertical resizes, mobile URL-bar show/hide).
    let frame = 0
    const handleResize = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        setWindowWidth((previous) =>
          previous === window.innerWidth ? previous : window.innerWidth
        )
      })
    }

    window.addEventListener('resize', handleResize)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const leftWidth = drawerOpen ? 232 : 88
  const rightWidth = drawerOpen2 ? rightSidebarWidth : 100
  const centerWidth = React.useMemo(() => {
    return Math.max(0, windowWidth - leftWidth - rightWidth)
  }, [windowWidth, leftWidth, rightWidth])

  const [connected, setConnected] = React.useState<boolean>(false)
  const [isLive, setIsLive] = React.useState<boolean>(false)

  const toggleDrawer = React.useCallback(() => {
    setDrawerOpen((prev) => !prev)
    if (drawerOpen2) {
      setDrawerOpen2(false)
      setIsRightDrawerOpen(false)
    }

    setIsLeftDrawerOpen(!isLeftDrawerOpen)
  }, [drawerOpen2, isLeftDrawerOpen, setIsLeftDrawerOpen, setIsRightDrawerOpen])

  const toggleDrawer2 = React.useCallback(
    (value?: boolean) => {
      setDrawerOpen2((prev) => (value !== undefined ? value : !prev))
      if (drawerOpen) {
        setDrawerOpen(false)
        setIsLeftDrawerOpen(false)
      }

      setIsRightDrawerOpen(value !== undefined ? value : !isRightDrawerOpen)
    },
    [drawerOpen, isRightDrawerOpen, setIsLeftDrawerOpen, setIsRightDrawerOpen]
  )

  // A fresh object literal here made every consumer of this context re-render
  // on every render of the provider — which, with the resize listener above,
  // meant the entire app re-rendered while a window was being dragged.
  const value = React.useMemo(
    () => ({
      drawerOpen,
      toggleDrawer,
      isLive,
      setIsLive,
      connected,
      setConnected,
      drawerOpen2,
      toggleDrawer2,
      rightSidebarWidth,
      setRightSidebarWidth,
      centerWidth
    }),
    [
      drawerOpen,
      toggleDrawer,
      isLive,
      connected,
      drawerOpen2,
      toggleDrawer2,
      rightSidebarWidth,
      setRightSidebarWidth,
      centerWidth
    ]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export default AppProvider
